import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './api';
import { promoterApi } from './promoterApi';
import type { TestimonialRequestType, TestimonialRequestStatus, ReferenceCallSubmissionStatus, RejectReferenceCallSubmissionRequest } from '@clareefai/types';
import type { ReferenceCallSubmissionResponse } from '@clareefai/types';
import { VendorResponse, PublicVendorResponse, PublicVendorReview, UpdateVendorRequest, MagicLinkVerificationResponse, SetPasswordRequest, QuestionResponse, UpdateQuestionsRequest, TestimonialResponse, TestimonialsByStatusResponse, UpdateReviewResponseRequest, DashboardStatsResponse, RewardResponse, RewardSummaryResponse, Meeting, RewardSettings, UpdateRewardSettingsRequest, SendBonusRequest, BonusRewardResponse, RewardAnalytics, SpecificRewardResponse, FundRequestResponse, CreateFundRequestRequest, TestimonialRequestResponse, CreateTestimonialRequestRequest, ApproveTestimonialRequestRequest, GenerateUseCaseForRequestRequest, UseCaseGenerationResponse, CreateDemoRequestDto, DemoRequestResponse, UpdateDemoRequestStatusDto, DemoRequestStats, AssignDemoRequestDto, PermissionsResponse, DiscountRewardResponse, RolePermissionsMatrix, UpdateRolePermissionsRequest as UpdateRolePermissionsDto, DefaultPermissionsResponse } from '@clareefai/types';
import type { ExtractionJobStatus } from '@clareefai/types';
import type { ReviewsWidgetConfig, UpdateReviewsWidgetConfigRequest } from '@clareefai/types';
import type { DealRoomSummaryResponse, DealRoomDetailResponse, CreateDealRoomRequest, UpdateDealRoomRequest } from '@clareefai/types';
import type { CheckPromoterEmailResponse, CreateSpecificRewardCommitmentRequest, SpecificRewardCommitmentResponse, SpecificRewardCommitmentListItem, SendReminderRequest } from '@clareefai/types';

// Paginated response interface
interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// DEV-166 — use-case publish/feature/approval changes live in vendorApi, but
// MyPage derives its published use cases from promoterApi's getPromoters. RTK
// Query tags don't cross api slices, so after these mutations succeed we
// explicitly refresh the promoter cache to pull the latest use-case state.
const refreshPromotersAfter = async (
  { dispatch, queryFulfilled }: { dispatch: (action: any) => void; queryFulfilled: Promise<unknown> }
) => {
  try {
    await queryFulfilled;
    dispatch(promoterApi.util.invalidateTags(['Promoter']));
  } catch {
    // Leave the cache untouched if the mutation failed.
  }
};

export const vendorApi = createApi({
  reducerPath: 'vendorApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Vendor', 'Testimonial', 'Dashboard', 'Reward', 'Meeting', 'ReferenceCallSubmissions', 'DemoRequest', 'Promoter', 'RolePermissions', 'Referral', 'SpecificRewardCommitment', 'EmailIntegration', 'Widget', 'ReviewsWidget', 'DealRoom'],
  endpoints: (builder) => ({
    getVendorMe: builder.query<VendorResponse, void>({
      query: () => '/vendors/me',
      providesTags: ['Vendor'],
    }),
    getPermissions: builder.query<PermissionsResponse, void>({
      query: () => '/vendors/me/permissions',
      providesTags: ['Vendor'],
    }),
    getPublicVendorBySlug: builder.query<PublicVendorResponse, string>({
      query: (slug) => `/vendors/public/by-slug/${slug}`,
    }),
    getVendorBalance: builder.query<{ balance: number; tremendousBalance?: number; lastUpdated?: string; pending_amount?: number; available_amount?: number }, void>({
      query: () => '/vendors/me/balance',
      providesTags: ['Vendor'],
    }),
    getDashboardStats: builder.query<DashboardStatsResponse, void>({
      query: () => '/vendors/me/dashboard/stats',
      providesTags: ['Dashboard'],
    }),
    updateVendor: builder.mutation<VendorResponse, UpdateVendorRequest>({
      query: (body) => ({
        url: '/vendors/me',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Vendor'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            vendorApi.util.updateQueryData('getVendorMe', undefined, () => data)
          );
        } catch {
          // Cache not updated on error
        }
      },
    }),
    verifyMagicLink: builder.query<MagicLinkVerificationResponse, string>({
      query: (token) => ({
        url: '/vendors/verify-magic-link',
        params: { token },
      }),
    }),
    setPassword: builder.mutation<VendorResponse, SetPasswordRequest>({
      query: (body) => ({
        url: '/vendors/set-password',
        method: 'POST',
        body,
      }),
    }),
    getUseCaseQuestions: builder.query<QuestionResponse[], void>({
      query: () => '/vendors/me/use-case-questions',
      providesTags: ['Vendor'],
    }),
    updateUseCaseQuestions: builder.mutation<VendorResponse, UpdateQuestionsRequest>({
      query: (body) => ({
        url: '/vendors/me/use-case-questions',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Vendor'],
    }),
    getReviewQuestions: builder.query<QuestionResponse[], void>({
      query: () => '/vendors/me/review-questions',
      providesTags: ['Vendor'],
    }),
    updateReviewQuestions: builder.mutation<VendorResponse, UpdateQuestionsRequest>({
      query: (body) => ({
        url: '/vendors/me/review-questions',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Vendor'],
    }),
    generateUseCaseForRequest: builder.mutation<UseCaseGenerationResponse, GenerateUseCaseForRequestRequest>({
      query: (body) => ({
        url: '/vendors/me/generate-use-case-for-request',
        method: 'POST',
        body,
      }),
    }),
    getAllTestimonials: builder.query<TestimonialsByStatusResponse, { type?: 'REVIEW' | 'VIDEO' | 'USE_CASE'; status?: 'COMPLETED' | 'PUBLISHED' | 'FEATURED' }>({
      query: ({ type, status }) => ({
        url: '/vendors/me/testimonials',
        params: { ...(type && { type }), ...(status && { status }) },
      }),
      providesTags: ['Testimonial'],
    }),
    getReviews: builder.query<TestimonialResponse[], { status?: 'COMPLETED' | 'PUBLISHED' | 'FEATURED' }>({
      query: ({ status }) => ({
        url: '/vendors/me/reviews',
        params: status ? { status } : {},
      }),
      providesTags: ['Testimonial'],
    }),
    getReviewById: builder.query<TestimonialResponse, { promoterId: string; testimonialId: string }>({
      query: ({ promoterId, testimonialId }) => ({
        url: `/vendors/me/promoters/${promoterId}/testimonials/${testimonialId}`,
      }),
      providesTags: (_result, _error, { testimonialId }) => [
        { type: 'Testimonial', id: testimonialId },
        'Testimonial',
      ],
    }),
    getUseCaseById: builder.query<TestimonialResponse, { promoterId: string; testimonialId: string }>({
      query: ({ promoterId, testimonialId }) => ({
        url: `/vendors/me/promoters/${promoterId}/testimonials/${testimonialId}`,
      }),
      providesTags: (_result, _error, { testimonialId }) => [
        { type: 'Testimonial', id: testimonialId },
        'Testimonial',
      ],
    }),
    getVideoTestimonialById: builder.query<TestimonialResponse, { promoterId: string; testimonialId: string }>({
      query: ({ promoterId, testimonialId }) => ({
        url: `/vendors/me/promoters/${promoterId}/testimonials/${testimonialId}`,
      }),
      providesTags: (_result, _error, { testimonialId }) => [
        { type: 'Testimonial', id: testimonialId },
        'Testimonial',
      ],
    }),
    getVideoTestimonials: builder.query<TestimonialResponse[], { status?: 'COMPLETED' | 'PUBLISHED' | 'FEATURED' }>({
      query: ({ status }) => ({
        url: '/vendors/me/video-testimonials',
        params: status ? { status } : {},
      }),
      providesTags: ['Testimonial'],
    }),
    getUseCases: builder.query<TestimonialsByStatusResponse, void>({
      query: () => ({ url: '/vendors/me/use-cases' }),
      providesTags: ['Testimonial'],
    }),
    toggleFeatureUseCase: builder.mutation<TestimonialResponse, { promoterId: string; testimonialId: string }>({
      query: ({ promoterId, testimonialId }) => ({
        url: `/vendors/me/promoters/${promoterId}/use-cases/${testimonialId}/feature`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, { testimonialId }) => [
        { type: 'Testimonial', id: testimonialId },
        'Testimonial',
      ],
      onQueryStarted: (_arg, api) => refreshPromotersAfter(api),
    }),
    toggleFeatureTestimonial: builder.mutation<TestimonialResponse, { promoterId: string; testimonialId: string }>({
      query: ({ promoterId, testimonialId }) => ({
        url: `/vendors/me/promoters/${promoterId}/testimonials/${testimonialId}/feature`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, { testimonialId }) => [
        { type: 'Testimonial', id: testimonialId },
        'Testimonial',
      ],
    }),
    updateReviewResponse: builder.mutation<TestimonialResponse, { promoterId: string; testimonialId: string; request: UpdateReviewResponseRequest }>({
      query: ({ promoterId, testimonialId, request }) => ({
        url: `/vendors/me/promoters/${promoterId}/testimonials/${testimonialId}/response`,
        method: 'PATCH',
        body: request,
      }),
      invalidatesTags: (_result, _error, { testimonialId }) => [
        { type: 'Testimonial', id: testimonialId },
        'Testimonial',
      ],
    }),
    generateReviewResponseSuggestion: builder.mutation<{ suggestion: string }, { promoterId: string; testimonialId: string }>({
      query: ({ promoterId, testimonialId }) => ({
        url: `/vendors/me/promoters/${promoterId}/testimonials/${testimonialId}/generate-response`,
        method: 'POST',
      }),
    }),
    publishTestimonial: builder.mutation<
      { testimonial_id: string; promoter_id: string; is_published: boolean; status: string; message: string },
      { promoterId: string; testimonialId: string; isPublished: boolean }
    >({
      query: ({ promoterId, testimonialId, isPublished }) => ({
        url: `/vendors/me/promoters/${promoterId}/testimonials/${testimonialId}/publish`,
        method: 'PUT',
        body: { is_published: isPublished },
      }),
      invalidatesTags: (_result, _error, { testimonialId }) => [
        { type: 'Testimonial', id: testimonialId },
        'Testimonial',
      ],
      onQueryStarted: (_arg, api) => refreshPromotersAfter(api),
    }),

    // ============ Reward Endpoints ============
    getVendorRewards: builder.query<PaginatedResponse<RewardResponse>, { page?: number; size?: number }>({
      query: ({ page = 0, size = 50 }) => ({
        url: '/vendors/me/rewards',
        params: { page, size },
      }),
      providesTags: ['Reward'],
    }),
    getVendorRewardsSummary: builder.query<RewardSummaryResponse, void>({
      query: () => '/vendors/me/rewards/summary',
      providesTags: ['Reward'],
    }),
    getPromoterRewards: builder.query<PaginatedResponse<RewardResponse>, { promoterId: string; page?: number; size?: number }>({
      query: ({ promoterId, page = 0, size = 20 }) => ({
        url: `/vendors/me/promoters/${promoterId}/rewards`,
        params: { page, size },
      }),
      providesTags: ['Reward'],
    }),
    payPendingReward: builder.mutation<RewardResponse, string>({
      query: (rewardId) => ({
        url: `/vendors/me/rewards/${rewardId}/pay`,
        method: 'POST',
      }),
      invalidatesTags: ['Reward'],
    }),
    declinePendingReward: builder.mutation<RewardResponse, string>({
      query: (rewardId) => ({
        url: `/vendors/me/rewards/${rewardId}/decline`,
        method: 'POST',
      }),
      invalidatesTags: ['Reward'],
    }),
    resendGiftCardEmail: builder.mutation<RewardResponse, string>({
      query: (rewardId) => ({
        url: `/vendors/me/rewards/${rewardId}/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['Reward'],
    }),
    cancelGiftCardReward: builder.mutation<RewardResponse, string>({
      query: (rewardId) => ({
        url: `/vendors/me/rewards/${rewardId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['Reward'],
    }),
    // ============ Reward Settings Endpoints ============
    getRewardSettings: builder.query<RewardSettings, void>({
      query: () => '/vendors/me/reward-settings',
      providesTags: ['Reward'],
    }),
    updateRewardSettings: builder.mutation<RewardSettings, UpdateRewardSettingsRequest>({
      query: (body) => ({
        url: '/vendors/me/reward-settings',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Reward'],
    }),
    sendBonusReward: builder.mutation<BonusRewardResponse[], SendBonusRequest>({
      query: (body) => ({
        url: '/vendors/me/rewards/bonus',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Reward'],
    }),
    getRewardAnalytics: builder.query<RewardAnalytics, void>({
      query: () => '/vendors/me/rewards/analytics',
      providesTags: ['Reward'],
    }),
    getSpecificRewards: builder.query<SpecificRewardResponse[], void>({
      query: () => '/vendors/me/rewards/specific',
      providesTags: ['Reward'],
    }),
    getReclaimedRewards: builder.query<RewardResponse[], void>({
      query: () => '/vendors/me/rewards/reclaimed',
      providesTags: ['Reward'],
    }),
    // ============ Discount Reward Endpoints ============
    getDiscountRewards: builder.query<DiscountRewardResponse[], void>({
      query: () => '/vendors/me/rewards/discounts',
      providesTags: ['Reward'],
    }),
    markDiscountApplied: builder.mutation<DiscountRewardResponse, string>({
      query: (rewardId) => ({
        url: `/vendors/me/rewards/${rewardId}/apply-discount`,
        method: 'POST',
      }),
      invalidatesTags: ['Reward'],
    }),
    // ============ Fund Request Endpoints ============
    createFundRequest: builder.mutation<FundRequestResponse, CreateFundRequestRequest>({
      query: (body) => ({
        url: '/vendors/me/fund-requests',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Vendor'],
    }),
    getFundRequests: builder.query<FundRequestResponse[], void>({
      query: () => '/vendors/me/fund-requests',
      providesTags: ['Vendor'],
    }),
    // ============ Meeting Endpoints ============
    getVendorMeetings: builder.query<Meeting[], { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/vendors/me/meetings',
        params: {
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      }),
      providesTags: ['Vendor'],
    }),
    // Public endpoint - no authentication required
    getPublicUseCase: builder.query<TestimonialResponse, string>({
      query: (useCaseId) => ({
        url: `/vendors/public/use-cases/${useCaseId}`,
      }),
    }),
    getPublicReview: builder.query<PublicVendorReview, string>({
      query: (reviewId) => `/vendors/public/reviews/${reviewId}`,
    }),
    getMyReview: builder.query<PublicVendorReview, string>({
      query: (reviewId) => `/vendors/me/reviews/${reviewId}`,
    }),
    // ============ Testimonial Request Endpoints ============
    createTestimonialRequest: builder.mutation<TestimonialRequestResponse, CreateTestimonialRequestRequest>({
      query: (body) => ({
        url: '/vendors/me/testimonial-requests',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Testimonial'],
    }),
    getTestimonialRequests: builder.query<TestimonialRequestResponse[], { type?: TestimonialRequestType; status?: TestimonialRequestStatus }>({
      query: ({ type, status }) => ({
        url: '/vendors/me/testimonial-requests',
        params: {
          ...(type && { type }),
          ...(status && { status }),
        },
      }),
      providesTags: ['Testimonial'],
    }),
    getTestimonialRequestById: builder.query<TestimonialRequestResponse, string>({
      query: (id) => ({
        url: `/vendors/me/testimonial-requests/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: 'Testimonial', id }],
    }),
    approveTestimonialRequest: builder.mutation<TestimonialRequestResponse, { id: string; request: ApproveTestimonialRequestRequest }>({
      query: ({ id, request }) => ({
        url: `/vendors/me/testimonial-requests/${id}/approve`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Testimonial'],
      onQueryStarted: (_arg, api) => refreshPromotersAfter(api),
    }),
    // DEV-92 — toggle the auto-reminder scheduler for a USE_CASE request
    setUseCaseAutoReminder: builder.mutation<{ autoReminderEnabled: boolean; reminderCount: number }, { id: string; enabled: boolean }>({
      query: ({ id, enabled }) => ({
        url: `/vendors/me/testimonial-requests/${id}/auto-reminder`,
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Testimonial', id }, 'Testimonial'],
    }),
    // DEV-92 — manually nudge the promoter now (ignores the 7-day gate)
    sendUseCaseReminderNow: builder.mutation<{ lastReminderSentAt: string; reminderCount: number }, string>({
      query: (id) => ({
        url: `/vendors/me/testimonial-requests/${id}/remind-now`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Testimonial', id }, 'Testimonial'],
    }),
    // Reference call submissions (promoter-submitted call frequency, vendor approves)
    getReferenceCallSubmissions: builder.query<ReferenceCallSubmissionResponse[], { status?: ReferenceCallSubmissionStatus }>({
      query: ({ status }) => ({
        url: '/vendors/me/reference-call-submissions',
        params: status ? { status } : {},
      }),
      providesTags: ['ReferenceCallSubmissions'],
    }),
    approveReferenceCallSubmission: builder.mutation<ReferenceCallSubmissionResponse, string>({
      query: (id) => ({
        url: `/vendors/me/reference-call-submissions/${id}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['ReferenceCallSubmissions', 'Promoter'],
    }),
    rejectReferenceCallSubmission: builder.mutation<ReferenceCallSubmissionResponse, { id: string; request?: RejectReferenceCallSubmissionRequest }>({
      query: ({ id, request }) => ({
        url: `/vendors/me/reference-call-submissions/${id}/reject`,
        method: 'POST',
        body: request ?? {},
      }),
      invalidatesTags: ['ReferenceCallSubmissions', 'Promoter'],
    }),
    // ============ Demo Request Endpoints ============
    createDemoRequest: builder.mutation<DemoRequestResponse, CreateDemoRequestDto>({
      query: (body) => ({
        url: '/vendors/public/demo-requests',
        method: 'POST',
        body,
      }),
    }),
    getDemoRequests: builder.query<DemoRequestResponse[], { status?: string }>({
      query: ({ status }) => ({
        url: '/vendors/me/demo-requests',
        params: status ? { status } : {},
      }),
      providesTags: ['DemoRequest'],
    }),
    updateDemoRequestStatus: builder.mutation<DemoRequestResponse, { id: string; status: string; notes?: string }>({
      query: ({ id, status, notes }) => ({
        url: `/vendors/me/demo-requests/${id}/status`,
        method: 'PATCH',
        body: { status, notes },
      }),
      invalidatesTags: ['DemoRequest'],
    }),
    getDemoRequestStats: builder.query<DemoRequestStats, void>({
      query: () => '/vendors/me/demo-requests/stats',
      providesTags: ['DemoRequest'],
    }),
    assignDemoRequest: builder.mutation<DemoRequestResponse, { id: string } & AssignDemoRequestDto>({
      query: ({ id, teamMemberId, teamMemberName }) => ({
        url: `/vendors/me/demo-requests/${id}/assign`,
        method: 'PATCH',
        body: { teamMemberId, teamMemberName },
      }),
      invalidatesTags: ['DemoRequest'],
    }),
    // ============ Role Permissions Endpoints ============
    getRolePermissions: builder.query<RolePermissionsMatrix, void>({
      query: () => '/vendors/me/role-permissions',
      providesTags: ['RolePermissions'],
    }),
    getDefaultPermissions: builder.query<DefaultPermissionsResponse, void>({
      query: () => '/vendors/me/role-permissions/defaults',
      providesTags: ['RolePermissions'],
    }),
    updateRolePermissions: builder.mutation<RolePermissionsMatrix, UpdateRolePermissionsDto>({
      query: (body) => ({
        url: '/vendors/me/role-permissions',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['RolePermissions', 'Vendor'],
    }),
    resetRolePermissions: builder.mutation<RolePermissionsMatrix, void>({
      query: () => ({
        url: '/vendors/me/role-permissions',
        method: 'DELETE',
      }),
      invalidatesTags: ['RolePermissions', 'Vendor'],
    }),
    // ============ Referral Endpoints ============
    getVendorReferrals: builder.query<import('@clareefai/types').ReferralResponse[], void>({
      query: () => '/vendors/me/referrals',
      providesTags: ['Referral'],
    }),
    getVendorReferralById: builder.query<import('@clareefai/types').ReferralResponse, string>({
      query: (id) => `/vendors/me/referrals/${id}`,
      providesTags: ['Referral'],
    }),
    updateReferralStatus: builder.mutation<import('@clareefai/types').ReferralResponse, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/vendors/me/referrals/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Referral'],
    }),
    resendReferralInvitation: builder.mutation<import('@clareefai/types').ReferralResponse, string>({
      query: (id) => ({
        url: `/vendors/me/referrals/${id}/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['Referral'],
    }),
    confirmReferralInvitation: builder.mutation<import('@clareefai/types').ReferralResponse, string>({
      query: (id) => ({
        url: `/vendors/me/referrals/${id}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: ['Referral'],
    }),
    assignReferral: builder.mutation<import('@clareefai/types').ReferralResponse, { id: string; team_member_id: string }>({
      query: ({ id, ...body }) => ({
        url: `/vendors/me/referrals/${id}/assign`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Referral'],
    }),
    pushReferralToCrm: builder.mutation<import('@clareefai/types').ReferralResponse, { id: string; provider: string }>({
      query: ({ id, ...body }) => ({
        url: `/vendors/me/referrals/${id}/push-crm`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Referral'],
    }),
    approveReferralReward: builder.mutation<import('@clareefai/types').ReferralResponse, string>({
      query: (id) => ({
        url: `/vendors/me/referrals/${id}/approve-reward`,
        method: 'POST',
      }),
      invalidatesTags: ['Referral', 'Reward'],
    }),
    // Specific Reward Commitment endpoints
    checkPromoterEmail: builder.mutation<CheckPromoterEmailResponse, { email: string }>({
      query: (body) => ({
        url: '/vendors/me/specific-reward-commitments/check-email',
        method: 'POST',
        body,
      }),
    }),
    createSpecificRewardCommitment: builder.mutation<SpecificRewardCommitmentResponse, CreateSpecificRewardCommitmentRequest>({
      query: (body) => ({
        url: '/vendors/me/specific-reward-commitments',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SpecificRewardCommitment'],
    }),
    getSpecificRewardCommitments: builder.query<SpecificRewardCommitmentListItem[], void>({
      query: () => '/vendors/me/specific-reward-commitments',
      providesTags: ['SpecificRewardCommitment'],
    }),
    getSpecificRewardCommitmentDetail: builder.query<SpecificRewardCommitmentResponse, string>({
      query: (id) => `/vendors/me/specific-reward-commitments/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'SpecificRewardCommitment', id }],
    }),
    sendSpecificRewardReminder: builder.mutation<{ status: string }, { id: string; body: SendReminderRequest }>({
      query: ({ id, body }) => ({
        url: `/vendors/me/specific-reward-commitments/${id}/remind`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'SpecificRewardCommitment', id }],
    }),
    cancelSpecificRewardCommitment: builder.mutation<{ status: string }, string>({
      query: (id) => ({
        url: `/vendors/me/specific-reward-commitments/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['SpecificRewardCommitment'],
    }),
    markSpecificRewardPaid: builder.mutation<{ status: string }, string>({
      query: (id) => ({
        url: `/vendors/me/specific-reward-commitments/${id}/mark-paid`,
        method: 'POST',
      }),
      invalidatesTags: ['SpecificRewardCommitment'],
    }),
    // Email Integration endpoints
    getEmailIntegrationStatus: builder.query<import('@clareefai/types').EmailIntegrationStatus, void>({
      query: () => '/vendors/me/email-integration/status',
      providesTags: ['EmailIntegration'],
    }),
    getGoogleAuthUrl: builder.query<import('@clareefai/types').EmailIntegrationAuthUrlResponse, void>({
      query: () => '/vendors/me/email-integration/google/auth-url',
    }),
    connectGoogleEmail: builder.mutation<import('@clareefai/types').EmailIntegrationStatus, { code: string }>({
      query: (body) => ({
        url: '/vendors/me/email-integration/google/callback',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmailIntegration'],
    }),
    getMicrosoftAuthUrl: builder.query<import('@clareefai/types').EmailIntegrationAuthUrlResponse, void>({
      query: () => '/vendors/me/email-integration/microsoft/auth-url',
    }),
    connectMicrosoftEmail: builder.mutation<import('@clareefai/types').EmailIntegrationStatus, { code: string }>({
      query: (body) => ({
        url: '/vendors/me/email-integration/microsoft/callback',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmailIntegration'],
    }),
    updateEmailDelegationCategories: builder.mutation<import('@clareefai/types').EmailIntegrationStatus, { enabledCategories: string[] }>({
      query: (body) => ({
        url: '/vendors/me/email-integration/categories',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['EmailIntegration'],
    }),
    sendTestEmail: builder.mutation<import('@clareefai/types').EmailIntegrationTestResponse, void>({
      query: () => ({
        url: '/vendors/me/email-integration/test',
        method: 'POST',
      }),
    }),
    disconnectEmailIntegration: builder.mutation<void, void>({
      query: () => ({
        url: '/vendors/me/email-integration/disconnect',
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailIntegration'],
    }),
    verifySmtpEmail: builder.mutation<import('@clareefai/types').SmtpVerifyResponse, import('@clareefai/types').SmtpConnectRequest>({
      query: (body) => ({
        url: '/vendors/me/email-integration/smtp/verify',
        method: 'POST',
        body,
      }),
    }),
    connectSmtpEmail: builder.mutation<import('@clareefai/types').EmailIntegrationStatus, import('@clareefai/types').SmtpConnectRequest>({
      query: (body) => ({
        url: '/vendors/me/email-integration/smtp/connect',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmailIntegration'],
    }),

    // External Review Extraction
    triggerReviewExtraction: builder.mutation<ExtractionJobStatus[], void>({
      query: () => ({
        url: '/vendors/me/extract-reviews',
        method: 'POST',
      }),
      invalidatesTags: ['Vendor'],
    }),
    getExtractionStatus: builder.query<ExtractionJobStatus[], void>({
      query: () => '/vendors/me/extraction-status',
    }),
    toggleProviderVisibility: builder.mutation<{ provider: string; visible: boolean }, { provider: string; visible: boolean }>({
      query: ({ provider, visible }) => ({
        url: `/vendors/me/external-review-providers/${provider}/visibility`,
        method: 'PATCH',
        body: { visible },
      }),
      invalidatesTags: ['Vendor'],
    }),
    // ============ Widget Config Endpoints ============
    getWidgetConfig: builder.query<WidgetConfigResponse, void>({
      query: () => '/vendors/me/widget',
      providesTags: ['Widget'],
    }),
    updateWidgetConfig: builder.mutation<WidgetConfigResponse, UpdateWidgetConfigRequest>({
      query: (body) => ({
        url: '/vendors/me/widget',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Widget'],
    }),
    // ============ Reviews Widget Config Endpoints ============
    getReviewsWidgetConfig: builder.query<ReviewsWidgetConfig, void>({
      query: () => '/vendors/me/reviews-widget',
      providesTags: ['ReviewsWidget'],
    }),
    updateReviewsWidgetConfig: builder.mutation<ReviewsWidgetConfig, UpdateReviewsWidgetConfigRequest>({
      query: (body) => ({
        url: '/vendors/me/reviews-widget',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ReviewsWidget'],
    }),
    // ============ Deal Room Endpoints ============
    getDealRooms: builder.query<DealRoomSummaryResponse[], void>({
      query: () => '/vendors/me/deal-rooms',
      providesTags: ['DealRoom'],
    }),
    getDealRoom: builder.query<DealRoomDetailResponse, string>({
      query: (id) => `/vendors/me/deal-rooms/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'DealRoom', id }],
    }),
    createDealRoom: builder.mutation<DealRoomDetailResponse, CreateDealRoomRequest>({
      query: (body) => ({
        url: '/vendors/me/deal-rooms',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DealRoom'],
    }),
    updateDealRoom: builder.mutation<DealRoomDetailResponse, { id: string; data: UpdateDealRoomRequest }>({
      query: ({ id, data }) => ({
        url: `/vendors/me/deal-rooms/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'DealRoom', id }, 'DealRoom'],
    }),
    deleteDealRoom: builder.mutation<void, string>({
      query: (id) => ({
        url: `/vendors/me/deal-rooms/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DealRoom'],
    }),
  }),
});

export interface WidgetConfigResponse {
  isEnabled: boolean;
  publicKey: string | null;
  buttonText: string | null;
  color: string | null;
  variant: string | null;
  position: string | null;
  impressions: number;
  signups: number;
  scriptUrl: string;
  signupUrl: string | null;
  snippet: string | null;
}

export interface UpdateWidgetConfigRequest {
  isEnabled?: boolean;
  buttonText?: string;
  color?: string;
  variant?: string;
  position?: string;
  regenerateKey?: boolean;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Direct fetch utility for PDF downloads — RTK Query can't store Blobs in Redux.
// Reads the Firebase token the same way the base query does.
const _TOKEN_KEY = 'firebase_id_token';
const _API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

export async function downloadReviewPdf(
  promoterId: string,
  testimonialId: string,
  filename: string
): Promise<void> {
  let token: string | null = null;
  try {
    const { auth } = await import('@clareefai/api');
    if (auth.currentUser) {
      token = await auth.currentUser.getIdToken();
    }
  } catch {
    // fall through to localStorage
  }
  if (!token) {
    token = localStorage.getItem(_TOKEN_KEY);
  }

  const response = await fetch(
    `${_API_BASE}/vendors/me/promoters/${promoterId}/testimonials/${testimonialId}/export/pdf`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  );

  if (!response.ok) {
    throw new Error(`Export failed (${response.status})`);
  }

  const blob = await response.blob();
  downloadBlob(blob, filename);
}

export const {
  useGetVendorMeQuery,
  useGetPermissionsQuery,
  useUpdateVendorMutation,
  useVerifyMagicLinkQuery,
  useSetPasswordMutation,
  useGetUseCaseQuestionsQuery,
  useUpdateUseCaseQuestionsMutation,
  useGetReviewQuestionsQuery,
  useUpdateReviewQuestionsMutation,
  useGetAllTestimonialsQuery,
  useGetReviewsQuery,
  useGetReviewByIdQuery,
  useGetUseCaseByIdQuery,
  useGetVideoTestimonialByIdQuery,
  useGetVideoTestimonialsQuery,
  useGetUseCasesQuery,
  useToggleFeatureUseCaseMutation,
  useToggleFeatureTestimonialMutation,
  useUpdateReviewResponseMutation,
  useGenerateReviewResponseSuggestionMutation,
  usePublishTestimonialMutation,
  useGetDashboardStatsQuery,
  // Reward hooks
  useGetVendorRewardsQuery,
  useGetVendorRewardsSummaryQuery,
  useGetPromoterRewardsQuery,
  usePayPendingRewardMutation,
  useDeclinePendingRewardMutation,
  useResendGiftCardEmailMutation,
  useCancelGiftCardRewardMutation,
  // Reward Settings hooks
  useGetRewardSettingsQuery,
  useUpdateRewardSettingsMutation,
  useSendBonusRewardMutation,
  useGetRewardAnalyticsQuery,
  useGetSpecificRewardsQuery,
  useGetReclaimedRewardsQuery,
  // Discount Reward hooks
  useGetDiscountRewardsQuery,
  useMarkDiscountAppliedMutation,
  // Fund Request hooks
  useCreateFundRequestMutation,
  useGetFundRequestsQuery,
  // Meeting hooks
  useGetVendorMeetingsQuery,
  // Balance hook
  useGetVendorBalanceQuery,
  // Public hooks
  useGetPublicVendorBySlugQuery,
  useGetPublicUseCaseQuery,
  // Testimonial Request hooks
  useCreateTestimonialRequestMutation,
  useGetTestimonialRequestsQuery,
  useGetTestimonialRequestByIdQuery,
  useApproveTestimonialRequestMutation,
  useSetUseCaseAutoReminderMutation,
  useSendUseCaseReminderNowMutation,
  // Generate use case for request (vendor answers questions → GPT)
  useGenerateUseCaseForRequestMutation,
  // Reference call submissions
  useGetReferenceCallSubmissionsQuery,
  useApproveReferenceCallSubmissionMutation,
  useRejectReferenceCallSubmissionMutation,
  // Demo Request hooks
  useCreateDemoRequestMutation,
  useGetDemoRequestsQuery,
  useUpdateDemoRequestStatusMutation,
  useGetDemoRequestStatsQuery,
  useAssignDemoRequestMutation,
  // Role Permissions hooks
  useGetRolePermissionsQuery,
  useGetDefaultPermissionsQuery,
  useUpdateRolePermissionsMutation,
  useResetRolePermissionsMutation,
  // Referral hooks
  useGetVendorReferralsQuery,
  useGetVendorReferralByIdQuery,
  useUpdateReferralStatusMutation,
  useResendReferralInvitationMutation,
  useConfirmReferralInvitationMutation,
  useAssignReferralMutation,
  usePushReferralToCrmMutation,
  useApproveReferralRewardMutation,
  // Specific Reward Commitment hooks
  useCheckPromoterEmailMutation,
  useCreateSpecificRewardCommitmentMutation,
  useGetSpecificRewardCommitmentsQuery,
  useGetSpecificRewardCommitmentDetailQuery,
  useSendSpecificRewardReminderMutation,
  useCancelSpecificRewardCommitmentMutation,
  useMarkSpecificRewardPaidMutation,
  // Email Integration hooks
  useGetEmailIntegrationStatusQuery,
  useGetGoogleAuthUrlQuery,
  useConnectGoogleEmailMutation,
  useGetMicrosoftAuthUrlQuery,
  useConnectMicrosoftEmailMutation,
  useUpdateEmailDelegationCategoriesMutation,
  useSendTestEmailMutation,
  useDisconnectEmailIntegrationMutation,
  useVerifySmtpEmailMutation,
  useConnectSmtpEmailMutation,
  // External Review Extraction hooks
  useTriggerReviewExtractionMutation,
  useGetExtractionStatusQuery,
  useToggleProviderVisibilityMutation,
  // Public review hook
  useGetPublicReviewQuery,
  useGetMyReviewQuery,
  // Widget config hooks
  useGetWidgetConfigQuery,
  useUpdateWidgetConfigMutation,
  useGetReviewsWidgetConfigQuery,
  useUpdateReviewsWidgetConfigMutation,
  // Deal Room hooks
  useGetDealRoomsQuery,
  useGetDealRoomQuery,
  useCreateDealRoomMutation,
  useUpdateDealRoomMutation,
  useDeleteDealRoomMutation,
} = vendorApi;

